// Catálogo da Jornada já SEM o que o escritório não contratou, e o progresso por
// nível calculado sobre ele.
//
// ★ Por que um mixin e não um getter Vuex: o veredito de contrato precisa de
// `router` (resolve a URL da etapa contra a `meta` da rota) e de `store`, e
// getter é `(state) => …`. Importar o singleton do router dentro de
// `store/getters.js` quebraria toda a suíte do módulo, que monta store falso sem
// router.
//
// ★ Por que não filtrar no API layer, gravando já filtrado no store: não seria
// REATIVO. `parameters/show` chega no `beforeEach` do router e o
// `GET /config-journey` do `created` pode resolver antes — um snapshot tirado
// nesse instante esconderia quase tudo e só se corrigiria no próximo poll (60s).
// Em computed, as dependências são `journeyModules` + `show` + `currentUser`, e
// a tela se conserta sozinha assim que as flags chegam.
import {
  filtrarModulosVisiveis,
  marcarVitrines,
  progressoPorNivel,
} from '@modules/central-config/utils/progress'
import {
  criarGateDeContrato,
  criarPredicadoDeVitrine,
  etapaBloqueadaPorContrato,
} from '@modules/central-config/utils/feature-gate'
import { rotaInicialDoModulo } from '@modules/central-config/utils/module-routes'

export const journeyVisibilityMixin = {
  computed: {
    // ⚠️ NUNCA acrescentar aqui dependência da coreografia de "Iniciar etapa"
    // (`navigatingTaskId` e afins): `router.resolve()` percorre a tabela de
    // rotas, e ligar isso à transição re-resolveria o catálogo inteiro a cada
    // passo dela. É a lição já paga pelo `lockedByUrl` do accordion.
    modulosVisiveis() {
      if (!this.$store) return []
      const modules = this.$store.getters['centralConfig/journeyModules'] || []
      const gate = criarGateDeContrato({
        router: this.$router,
        store: this.$store,
      })
      // ★ Só a VITRINE escapa do gate (2026-08-07 — decisão do owner com os
      // dados na mão). Ela declara `premium_feature` e é upsell deliberado:
      // aparece esmaecida, inerte, fora do denominador.
      //
      // A obrigatória BARRADA some — inclusive para o não-gestor, que é quem a
      // via. Entre 05/08 e 07/08 ela ficava visível (marcada `indisponivel`)
      // para não sumir a única forma de destravar o nível, já que o backend
      // promove por `done >= total` sobre TODAS as obrigatórias. Duas coisas
      // derrubaram esse desenho:
      //
      // 1. Medição: das 57 barradas da assessora no escritório de teste, **57**
      //    eram apenas o PAPEL — o escritório tem tudo. Não era informação de
      //    contratação, era a lista de tarefas de outra pessoa.
      // 2. `nivelDoModulo` deixou de travar em pendente inalcançável
      //    (a62ccf6ac), então o card do módulo já não depende delas para andar.
      //
      // ⚠️ Consequência aceita: o anel e `progressoPorNivelVisivel` passam a
      // contar só o que a pessoa vê — coerentes entre si e com a tela. O
      // `nivel_atual` que o BACKEND devolve continua contando todas as
      // obrigatórias, então ele pode ficar atrás do que a tela mostra até o
      // gestor concluir as dele (em produção, escopo `escritorio` da spec-005
      // faz isso aparecer concluído aqui). Quem precisa da lista escondida usa o
      // relatório: ~/aawz-tmp/jornada/etapas-que-so-o-gestor-faz.xlsx
      //
      // A OPCIONAL barrada já sumia pelo mesmo caminho.
      const ehVitrine = criarPredicadoDeVitrine({ store: this.$store })

      const marcados = marcarVitrines(modules, ehVitrine)

      const escapaDoGate = (task) => !!((task && task.metadata) || {}).vitrine
      const estaBloqueada = (task) =>
        !escapaDoGate(task) && gate(task && task.url)
      // O módulo inteiro é barrado quando a ROTA INICIAL dele é barrada — mesmo
      // que alguma etapa continue acessível. É o caso do AAWZ Drive não
      // contratado: `/aawz-drive/arquivos` é gateado, mas `/aawz-drive/bem-vindo`
      // libera como upsell (`customPermission`), e o módulo aparecia na Jornada
      // com uma trilha de 1 etapa cujo destino é a página de venda.
      // Reusa `MODULE_KEY_TO_ROUTE`, então não há mapa slug→flag novo para
      // apodrecer: o mesmo `name` que o botão "voltar ao módulo" usa.
      // Fora do memo do `gate` de propósito: ele é chaveado por URL string, e um
      // objeto de location nunca acertaria o cache. São ~11 chamadas por
      // avaliação (uma por módulo), contra ~184 das etapas.
      const moduloBloqueado = (module) => {
        const rota = rotaInicialDoModulo(module && module.slug)
        if (!rota) return false
        return etapaBloqueadaPorContrato({
          router: this.$router,
          store: this.$store,
          url: { name: rota },
        })
      }
      return filtrarModulosVisiveis(marcados, estaBloqueada, moduloBloqueado)
    },
    // Mapa `{[nivel]: {done,total,percent}}` — mapa e não lista pelo mesmo
    // motivo do getter que ele substitui: evita `.find()` dentro de v-for.
    progressoPorNivelVisivel() {
      const mapa = {}
      progressoPorNivel(this.modulosVisiveis).forEach((item) => {
        mapa[item.nivel] = item
      })
      return mapa
    },
  },
}
